// src/hooks/sync/useChallengeRequirements.js
// ─────────────────────────────────────────────────────────────────────────────
//  Milestone requirements for challenges whose description names a real action
//  (add a disciple, record a tithe, ...). A challenge may collect every check-in
//  day, but it only completes — automatically or via "Mark Complete" — once that
//  action exists in the user's data.
//
//  unmetRequirement(templateId) → user-facing message, or null when the challenge
//  has no requirement or the requirement is already met.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback } from 'react';
import { useSyncStore } from './useSyncStore';
import { useDiscipleshipTracking } from '@/hooks/feature/useDiscipleshipTracking';
import { STORE_TITHES_OFFERING, STORE_OUTGOING_SUPPORT } from '@/lib/storeKeys';

// `disciples` is the useDiscipleshipTracking() shape (.salvation, .ledPrayer, .ledStudy).
const REQUIREMENTS = {
  add_first_disciple: {
    message: 'Add your first disciple in Discipleship Keeper to complete this challenge.',
    met: ({ disciples }) => disciples.length >= 1,
  },
  tithe_commitment: {
    message: 'Record a tithe in the Budget Ledger to complete this challenge.',
    met: ({ tithes }) => tithes.length >= 1,
  },
  support_missionary: {
    message: 'Record outgoing support in the Budget Ledger to complete this challenge.',
    met: ({ outgoing }) => outgoing.length >= 1,
  },
  disciple_to_salvation: {
    message: "Mark a disciple's salvation in Discipleship Keeper to complete this challenge.",
    met: ({ disciples }) => disciples.some(d => d?.salvation),
  },
  disciple_leads_out: {
    message: 'Mark a disciple as having led prayer or a study in Discipleship Keeper to complete this challenge.',
    met: ({ disciples }) => disciples.some(d => d?.ledPrayer || d?.ledStudy),
  },
};

export function useChallengeRequirements() {
  const { disciples } = useDiscipleshipTracking();
  const [tithes, , tithesReady] = useSyncStore(STORE_TITHES_OFFERING, []);
  const [outgoing, , outgoingReady] = useSyncStore(STORE_OUTGOING_SUPPORT, []);
  const ready = tithesReady && outgoingReady;

  const unmetRequirement = useCallback((templateId) => {
    const req = REQUIREMENTS[templateId];
    if (!req) return null;
    const data = {
      disciples: Array.isArray(disciples) ? disciples : [],
      tithes:    Array.isArray(tithes)    ? tithes    : [],
      outgoing:  Array.isArray(outgoing)  ? outgoing  : [],
    };
    return req.met(data) ? null : req.message;
  }, [disciples, tithes, outgoing]);

  return { unmetRequirement, ready };
}